/**
 * Login form: email or phone identifier plus password.
 */
import * as React from 'react';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { Button } from '@/components/forms/Button';
import { Checkbox } from '@/components/forms/Checkbox';
import { type LoginMode } from '@/hooks/useLogin';
import { useDefaultPhoneCountry } from '@/utils/user-contact-utils';
import { useTheme } from '@/hooks/useTheme';
import { Lock, LogIn, Send } from 'lucide-react';
import {
  getIdentifierFieldError,
  getIdentifierFieldState,
  getIdentifierIcon,
  getLoginPasswordError,
  getLoginPasswordState,
  matchPhonePrefix,
} from '@/components/auth/auth-utils';

/** Props for {@link LoginForm}. */
interface LoginFormProps {
  logo?: string;
  mode: LoginMode;
  identifier: string;
  password: string;
  rememberMe: boolean;
  isLoading: boolean;
  identifierTouched: boolean;
  passwordTouched: boolean;
  onIdentifierChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (checked: boolean) => void;
  onIdentifierBlur: () => void;
  onPasswordBlur: () => void;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
  onForgotPassword: () => void;
  onSwitchToPhone: (phoneNumber: string) => void;
}

/** Email/phone sign-in form; a leading `+dial` switches the field into phone mode. */
export const LoginForm: React.FC<LoginFormProps> = ({
  logo,
  mode,
  identifier,
  password,
  rememberMe,
  isLoading,
  identifierTouched,
  passwordTouched,
  onIdentifierChange,
  onPasswordChange,
  onRememberMeChange,
  onIdentifierBlur,
  onPasswordBlur,
  onSubmit,
  onForgotPassword,
  onSwitchToPhone,
}) => {
  const defaultCountry = useDefaultPhoneCountry();
  const { logoAlt } = useTheme();
  const [detectedCountry, setDetectedCountry] = React.useState<string>(defaultCountry);

  /** Detect an international prefix and hand the national number to phone mode. */
  function handleInitialIdentifierChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    const match = matchPhonePrefix(value);
    if (match) {
      setDetectedCountry(match.country);
      onSwitchToPhone(match.nationalNumber);
      return;
    }
    onIdentifierChange(value);
  }

  function handlePhoneIdentifierChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onIdentifierChange(e.target.value);
  }

  function handlePasswordInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onPasswordChange(e.target.value);
  }

  function renderLogo(): React.ReactNode {
    if (!logo) return null;
    return (
      <div className="login-logo-container">
        <img src={logo} alt={logoAlt} className="login-logo-main" />
      </div>
    );
  }

  function renderIdentifierField(): React.ReactNode {
    if (mode === 'phone') {
      return (
        <PhoneInput
          label="Numéro de téléphone"
          value={identifier}
          onChange={handlePhoneIdentifierChange}
          onBlur={onIdentifierBlur}
          countryCode={detectedCountry}
          disabled={isLoading}
          required
          autoFocus
        />
      );
    }
    return (
      <Input
        type="text"
        label="Email ou téléphone"
        placeholder="Veuillez saisir votre email ou téléphone"
        value={identifier}
        onChange={handleInitialIdentifierChange}
        onBlur={onIdentifierBlur}
        icon={getIdentifierIcon(mode)}
        iconPosition="left"
        state={getIdentifierFieldState(mode, identifier, identifierTouched)}
        error={getIdentifierFieldError(mode, identifier, identifierTouched)}
        disabled={isLoading}
        required
        autoFocus
      />
    );
  }

  function renderEmailExtras(): React.ReactNode {
    if (mode !== 'email') return null;
    return (
      <>
        <div className="login-field input-morph">
          <Input
            type="password"
            label="Mot de passe"
            placeholder="Entrez votre mot de passe"
            value={password}
            onChange={handlePasswordInputChange}
            onBlur={onPasswordBlur}
            icon={Lock}
            iconPosition="left"
            state={getLoginPasswordState(password, passwordTouched)}
            error={getLoginPasswordError(password, passwordTouched)}
            disabled={isLoading}
            required
            showPasswordToggle
          />
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label="Rester connecté"
            checked={rememberMe}
            onCheckedChange={onRememberMeChange}
            disabled={isLoading}
          />

          <button type="button" onClick={onForgotPassword} className="forgot-password-link">
            Mot de passe oublié ?
          </button>
        </div>
      </>
    );
  }

  function renderPhoneExtras(): React.ReactNode {
    if (mode !== 'phone') return null;
    return (
      <div className="flex justify-start">
        <Checkbox
          label="Rester connecté"
          checked={rememberMe}
          onCheckedChange={onRememberMeChange}
          disabled={isLoading}
        />
      </div>
    );
  }

  return (
    <div className="login-view">
      {renderLogo()}
      <h1 className="login-card-title">Bienvenue</h1>
      <p className="login-card-subtitle">Connectez-vous pour accéder à votre espace</p>

      <form onSubmit={onSubmit} className="login-form">
        <div className="login-field">{renderIdentifierField()}</div>
        {renderEmailExtras()}
        {renderPhoneExtras()}

        <div className="login-field">
          <Button
            type="submit"
            loading={isLoading}
            disabled={
              isLoading || mode === 'initial' || !identifier || (mode === 'email' && !password)
            }
            fullWidth
            variant="primary"
            size="lg"
            className="login-button"
            icon={mode === 'phone' ? Send : LogIn}
            iconPosition="left"
          >
            {mode === 'phone' ? 'Envoyer le code' : 'Se connecter'}
          </Button>
        </div>

        <div className="mt-6 text-center">
          <p className="login-footer-legal">
            En vous connectant, vous acceptez nos conditions d'utilisation
          </p>
        </div>
      </form>
    </div>
  );
};
